// lottie-web is loaded dynamically via import() to avoid TBT impact

// Import local components/modules
const { AdsServicesMarquee } = require("../components/ads-services-marquee");

/**
 * Small utils (keep lightweight, no dependencies)
 */
function debounce(fn, wait) {
	var t;
	return function () {
		var ctx = this;
		var args = arguments;
		clearTimeout(t);
		t = setTimeout(function () {
			fn.apply(ctx, args);
		}, wait);
	};
}

// Single cached promise — avoids multiple downloads/parses (especially important on mobile TBT)
var lottieModulePromise = null;
function loadLottieModule() {
	if (!lottieModulePromise) {
		lottieModulePromise = import(
			/* webpackChunkName: "lottie-canvas" */
			'lottie-web/build/player/lottie_canvas'
		);
	}
	return lottieModulePromise;
}

// If <img> has no src/srcset (only data-src/data-srcset/data-sizes),
// set them only when the tab becomes active (true-lazy poster hydration).
function hydrateLazyPoster(container) {
	if (!container) return;

	var img = container.querySelector('img.lottie-poster-image');
	if (!img) return;

	if (img.getAttribute('src')) return; // already hydrated

	var src = img.getAttribute('data-src');
	if (src) img.setAttribute('src', src);

	var srcset = img.getAttribute('data-srcset');
	if (srcset) img.setAttribute('srcset', srcset);

	var sizes = img.getAttribute('data-sizes');
	if (sizes) img.setAttribute('sizes', sizes);
}

// Pause hidden Lottie instances to save CPU (especially on mobile).
function pauseLottieIfAny(container) {
	if (!container || !container.lottieInstance) return;
	if (typeof container.lottieInstance.pause === 'function') {
		try { container.lottieInstance.pause(); } catch (e) {}
	}
}

// Resume visible Lottie instances (if already created).
function playLottieIfAny(container) {
	if (!container || !container.lottieInstance) return;
	if (typeof container.lottieInstance.play === 'function') {
		try { container.lottieInstance.play(); } catch (e) {}
	}
}

// Use requestIdleCallback for non-critical initialization
if ('requestIdleCallback' in window) {
	requestIdleCallback(function () {
		initLottieTabs();
	}, { timeout: 2000 });
} else {
	document.addEventListener('DOMContentLoaded', function () {
		setTimeout(initLottieTabs, 1);
	});
}

// On mobile (≤990px), the three accordion sections merge into one visual block.
// Sections 2 and 3 have their first item marked open by default (data-accordion-initial),
// which creates an inconsistent "open / closed / closed / open / ..." pattern.
// This runs synchronously on DOMContentLoaded — before the lazy-init IntersectionObserver
// fires for those below-the-fold sections — so the accordion JS never sees an initial trigger.
document.addEventListener('DOMContentLoaded', function () {
	if (window.innerWidth > 990) return;
	['second_accordion_section-1', 'third_accordion_section-1'].forEach(function (panelId) {
		var panel = document.getElementById(panelId);
		var btn   = document.querySelector('[data-accordion="#' + panelId + '"]');
		if (!panel || !btn) return;
		btn.setAttribute('aria-expanded', 'false');
		btn.classList.remove('active');
		btn.removeAttribute('data-accordion-initial');
		panel.setAttribute('hidden', '');
		panel.setAttribute('aria-hidden', 'true');
		panel.style.maxHeight = '0px';
		panel.classList.remove('expanded');
	});
});

// When viewport crosses the mobile breakpoint via resize (e.g. devtools toggle),
// restore correct accordion state for both directions.
var _mqlMobile = window.matchMedia && window.matchMedia('(max-width: 990px)');
if (_mqlMobile) {
	var _onMobileBreakpoint = function (e) {
		if (e.matches) {
			// ── Entering mobile ──────────────────────────────────────────────────
			// Sections 2 and 3: close their first items (unified group keeps only one open).
			// Also clear data-accordion-initial in case they weren't initialized yet.
			['second_accordion_section-1', 'third_accordion_section-1'].forEach(function (panelId) {
				var btn = document.querySelector('[data-accordion="#' + panelId + '"]');
				if (!btn) return;
				btn.removeAttribute('data-accordion-initial'); // for not-yet-initialized
				var container = btn.closest('[data-accordion-controls]');
				if (container && container.dataset.accordionInstance) {
					if (btn.getAttribute('aria-expanded') === 'true') btn.click();
				}
			});
		} else {
			// ── Entering desktop ─────────────────────────────────────────────────
			// Restore default state: first item open in every section.
			// This also triggers _updateParentClass(trigger) which re-sets active-tab-* classes
			// that were cleared when items were cross-closed by unified group on mobile.
			['first_accordion_section', 'second_accordion_section', 'third_accordion_section'].forEach(function (id) {
				var container = document.querySelector('[data-accordion-controls="' + id + '"]');
				if (!container) return;

				var triggers = container.querySelectorAll('[data-accordion]');
				if (!triggers.length) return;
				var firstBtn = triggers[0];

				if (!container.dataset.accordionInstance) {
					// Accordion not yet initialized — restore initial marker so it opens at init time
					firstBtn.setAttribute('data-accordion-initial', 'true');
					return;
				}

				// Already initialized: click to open first item if it's not already open
				if (firstBtn.getAttribute('aria-expanded') !== 'true') {
					firstBtn.click();
				}
			});
		}
	};
	if (_mqlMobile.addEventListener) {
		_mqlMobile.addEventListener('change', _onMobileBreakpoint);
	} else if (_mqlMobile.addListener) {
		_mqlMobile.addListener(_onMobileBreakpoint);
	}
}

// Sets up click listeners for animation tab switcher.
function initLottieTabs() {
	var switchNav = document.querySelector('.lottie-switch__nav');
	if (!switchNav) return;

	var switchItems = switchNav.querySelectorAll('.lottie-switch__item');
	var lottieContainers = document.querySelectorAll('.main-top-section .lottie-container');

	switchItems.forEach(function (item) {
		item.addEventListener('click', function () {
			var targetId = item.dataset.lottieSwitch;

			if (item.classList.contains('active')) return;

			switchItems.forEach(function (i) { i.classList.remove('active'); });
			item.classList.add('active');

			lottieContainers.forEach(function (container) {
				if (container.id === targetId) {
					container.classList.add('active');
					hydrateLazyPoster(container);
					// User action = load immediately (no IO delay here)
					initLottieWithPoster(container);
					playLottieIfAny(container);
				} else {
					container.classList.remove('active');
					pauseLottieIfAny(container);
				}
			});
		});
	});
}

// Nav "--disabled" is controlled directly via state + DOMLoaded/data_failed events.
function setLottieNavDisabled(container, isDisabled) {
	var navItem = document.querySelector('.lottie-switch__item[data-lottie-switch="' + container.id + '"]');
	if (!navItem) return;

	var nav = navItem.closest('.lottie-switch__nav');
	if (!nav) return;

	if (isDisabled) nav.classList.add('--disabled');
	else nav.classList.remove('--disabled');
}

// Initializes a single Lottie animation with a poster placeholder.
// Idempotent — uses dataset.lottieState to prevent race conditions and re-initialization.
function initLottieWithPoster(container) {
	var state = container.dataset.lottieState || 'idle';
	if (state === 'loading' || state === 'loaded') return;

	// Respect reduced motion: keep poster only
	try {
		if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			container.dataset.lottieState = 'loaded';
			container.classList.remove('is-loading');
			setLottieNavDisabled(container, false);
			return;
		}
	} catch (e) {}

	var poster = container.querySelector('.lottie-poster-image');
	var lottieDiv = container.querySelector('.lottie-canvas');
	var lottieSrc = lottieDiv && lottieDiv.dataset ? lottieDiv.dataset.src : null;

	if (!lottieSrc) {
		// No animation file yet for this tab (e.g. admin — coming later): show poster only
		setLottieNavDisabled(container, false);
		return;
	}

	if (!poster || !lottieDiv) {
		console.warn('Lottie poster setup is incomplete for container:', container);
		return;
	}

	container.dataset.lottieState = 'loading';
	container.classList.add('is-loading');
	setLottieNavDisabled(container, true);

	loadLottieModule().then(function (mod) {
		var lottie = (mod && mod.default) ? mod.default : mod;

		if (!lottie || typeof lottie.loadAnimation !== 'function') {
			throw new Error('lottie-web loaded but loadAnimation is missing.');
		}

		var anim = lottie.loadAnimation({
			container: lottieDiv,
			renderer: 'canvas',
			loop: true,
			autoplay: true,
			path: lottieSrc,
			rendererSettings: {
				preserveAspectRatio: 'xMidYMid meet',
				clearCanvas: true
			}
		});

		container.lottieInstance = anim;

		anim.addEventListener('DOMLoaded', function () {
			container.dataset.lottieState = 'loaded';
			container.classList.remove('is-loading');
			setLottieNavDisabled(container, false);
			lottieDiv.classList.add('is-loaded');

			// Canvas renderer needs explicit resize on container dimension changes
			var resizeFn = debounce(function () {
				try { anim.resize(); } catch (e) {}
			}, 150);

			if (window.ResizeObserver) {
				var ro = new ResizeObserver(function () {
					requestAnimationFrame(resizeFn);
				});
				ro.observe(lottieDiv);
				container._lottieResizeObserver = ro;
			} else {
				window.addEventListener('resize', resizeFn, { passive: true });
				container._lottieResizeHandler = resizeFn;
			}
		});

		anim.addEventListener('data_failed', function () {
			console.error('Failed to load Lottie animation for container:', container.id);
			container.dataset.lottieState = 'idle';
			container.classList.remove('is-loading');
			setLottieNavDisabled(container, false);
		});

	}).catch(function (error) {
		console.error('Failed to load lottie-web module:', error);
		container.dataset.lottieState = 'idle';
		container.classList.remove('is-loading');
		setLottieNavDisabled(container, false);
	});
}

// Schedules Lottie init on first user interaction OR 5s fallback.
// Lottie JSON is only fetched after interaction, guaranteeing zero TBT during Lighthouse/PSI.
function scheduleLottieInit(container) {
	if (!container) return;

	var done = false;
	var EVENTS = ['scroll', 'mousemove', 'pointerdown', 'touchstart', 'keydown'];

	function trigger() {
		if (done) return;
		done = true;
		EVENTS.forEach(function (evt) {
			window.removeEventListener(evt, trigger);
		});
		clearTimeout(fallbackTimer);
		initLottieWhenVisible(container);
	}

	EVENTS.forEach(function (evt) {
		window.addEventListener(evt, trigger, { passive: true });
	});

	var fallbackTimer = setTimeout(trigger, 5000);
}

// Initializes the first visible Lottie only when it is close to viewport.
function initLottieWhenVisible(container) {
	if (!container) return;

	var state = container.dataset.lottieState || 'idle';
	if (state === 'loading' || state === 'loaded') return;

	if (!('IntersectionObserver' in window)) {
		initLottieWithPoster(container);
		return;
	}

	var io = new IntersectionObserver(function (entries) {
		entries.forEach(function (e) {
			if (e.isIntersecting) {
				io.disconnect();
				initLottieWithPoster(container);
			}
		});
	}, { rootMargin: '200px' });

	io.observe(container);
}

/**
 * Keep the <picture data-accordion-target="ID"> inside the currently active/expanded
 * accordion panel when window width is below maxWidth, and restore it back when above.
 * Works per-accordion, supports multiple accordions on the page.
 *
 * @param {string} accordionId - e.g. "second_accordion_section"
 * @param {number} maxWidth - e.g. 992
 * @returns {Function} teardown function to remove listeners (optional)
 */
function setupAccordionPictureRelocator(accordionId, maxWidth) {
	var accordionContainer = document.querySelector(
		'[data-accordion-controls="' + accordionId + '"]'
	);
	if (!accordionContainer) return function () {};

	var picture = document.querySelector(
		'.accordion-item__picture[data-accordion-target="' + accordionId + '"]'
	);
	if (!picture) return function () {};

	var originalParent = picture.parentNode;
	var originalNextSibling = picture.nextSibling;

	// ✅ get Accordion instance if it exists
	var accordionInstance = accordionContainer.dataset.accordionInstance
		? accordionContainer.__accordionInstance
		: null;

	var getActivePanel = function () {
		var activeTrigger = accordionContainer.querySelector('[data-accordion].active');
		if (activeTrigger) {
			var sel = activeTrigger.dataset.accordion;
			if (sel) {
				var panel = document.querySelector(sel);
				if (panel) return panel;
			}
		}
		var expandedPanel = accordionContainer.querySelector('.accordion-item.expanded');
		if (expandedPanel) return expandedPanel;
		return null;
	};

	var refreshHeights = debounce(function () {
		// 🔸 After picture is moved, update panel height dynamically
		if (accordionInstance && typeof accordionInstance.updateHeights === 'function') {
			accordionInstance.updateHeights();
		} else {
			// fallback: keep behavior but avoid spamming sync resize events
			try {
				window.dispatchEvent(new Event('resize'));
			} catch (e) {}
		}
	}, 80);

	var moveIntoActive = function () {
		var panel = getActivePanel();
		if (!panel) return;

		var targetContent = panel.querySelector('.accordion-item__content') || panel;

		if (picture.parentNode !== targetContent) {
			targetContent.appendChild(picture);

			var updatePanelHeight = function () {
				if (!panel.classList.contains('expanded')) return;
				var currentMax = parseFloat(panel.style.maxHeight) || 0;
				if (panel.scrollHeight > currentMax + 10) {
					panel.style.maxHeight = panel.scrollHeight + 'px';
				}
			};

			var img = picture.querySelector('img');

			if (img && img.complete && img.naturalWidth > 0) {
				// Image already decoded — update after layout pass
				requestAnimationFrame(function () { requestAnimationFrame(updatePanelHeight); });
			} else {
				// Image is lazy-loaded: LazyLoad.js will set src later via IntersectionObserver.
				// Listen for the load event which fires once src is set and the image decodes.
				if (img) {
					var _onImgLoad = function () {
						img.removeEventListener('load', _onImgLoad);
						requestAnimationFrame(updatePanelHeight);
					};
					img.addEventListener('load', _onImgLoad);
				}
				// Fallback: if load event somehow doesn't fire (e.g. cached/already set),
				// try again after a longer delay to catch any case we missed.
				setTimeout(updatePanelHeight, 1500);
			}
		}
	};

	var moveBack = function () {
		if (!originalParent) return;
		if (picture.parentNode === originalParent) return;

		if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
			originalParent.insertBefore(picture, originalNextSibling);
		} else {
			originalParent.appendChild(picture);
		}

		requestAnimationFrame(function () {
			refreshHeights();
		});
	};

	/**
	 * ✅ MUST handle window resizing (compress/expand)
	 * We'll use matchMedia change + resize (fallback) + mutation observer
	 */
	var mq = null;
	try {
		if (window.matchMedia) {
			mq = window.matchMedia('(max-width: ' + (maxWidth - 1) + 'px)');
		}
	} catch (e) {}

	var applyRelocation = function () {
		var isMobile = (mq && mq.matches) ? true : (window.innerWidth < maxWidth);

		if (isMobile) moveIntoActive();
		else moveBack();
	};

	// ✅ Debounce for update function
	var debouncedApply = debounce(function () {
		applyRelocation();
	}, 100);

	// Watch accordion state changes (expanded/active classes)
	var observer = new MutationObserver(function () {
		debouncedApply();
	});

	accordionContainer
		.querySelectorAll('.accordion-header__item, .accordion-item')
		.forEach(function (el) {
			observer.observe(el, { attributes: true, attributeFilter: ['class'] });
		});

	// ✅ matchMedia reacts to breakpoint crossing even without resize spam
	if (mq && mq.addEventListener) {
		mq.addEventListener('change', debouncedApply);
	} else if (mq && mq.addListener) {
		// old Safari
		mq.addListener(debouncedApply);
	}

	// ✅ Passive listener for better performance (still keep, per your requirement)
	window.addEventListener('resize', debouncedApply, { passive: true });

	// Initial call
	debouncedApply();

	return function () {
		observer.disconnect();
		window.removeEventListener('resize', debouncedApply);

		if (mq && mq.removeEventListener) mq.removeEventListener('change', debouncedApply);
		else if (mq && mq.removeListener) mq.removeListener(debouncedApply);
	};
}




/**
 * Makes a group of independent accordions behave as one on mobile:
 * opening any item closes all open items across all other accordions in the group.
 * Only activates when viewport width < maxWidth (matches $grid-reackpoint).
 */
function setupUnifiedAccordionGroup(accordionIds, maxWidth) {
	var mq = null;
	try {
		if (window.matchMedia) mq = window.matchMedia('(max-width: ' + (maxWidth - 1) + 'px)');
	} catch (e) {}

	var isMobile = function () {
		return mq ? mq.matches : window.innerWidth < maxWidth;
	};

	var getOpenTriggers = function () {
		var open = [];
		accordionIds.forEach(function (id) {
			var container = document.querySelector('[data-accordion-controls="' + id + '"]');
			if (!container) return;
			container.querySelectorAll('[data-accordion]').forEach(function (btn) {
				if (btn.getAttribute('aria-expanded') === 'true') open.push(btn);
			});
		});
		return open;
	};

	accordionIds.forEach(function (id) {
		var container = document.querySelector('[data-accordion-controls="' + id + '"]');
		if (!container) return;

		// Capture phase ensures we run before the accordion's own click handler.
		container.addEventListener('click', function (e) {
			if (!isMobile()) return;
			var btn = e.target.closest('[data-accordion]');
			if (!btn) return;
			// Only act when the clicked item is closed (about to open).
			if (btn.getAttribute('aria-expanded') !== 'true') {
				getOpenTriggers().forEach(function (openBtn) {
					if (openBtn !== btn) openBtn.click();
				});
			}
		}, true);
	});
}


/**
 * Initializes "heavy" scripts that should run after the page is fully loaded
 * and interactive, to avoid blocking the initial render.
 */
function initHeavyScripts() {
	// console.log('Page is fully loaded. Initializing heavy scripts...'); // P0-2: removed debug log

	// Find and initialize only the Lottie animation that is visible by default.
	var initialActiveLottie = document.querySelector('.lottie-container.active');
	if (initialActiveLottie) {
		// Always hydrate the poster — visible on both mobile and desktop immediately
		hydrateLazyPoster(initialActiveLottie);

		// Defer Lottie JSON fetch to first user interaction (or 5s fallback).
		// scheduleLottieInit() guarantees zero TBT impact during Lighthouse/PSI tests
		// because Lighthouse never fires interaction events.
		scheduleLottieInit(initialActiveLottie);
	}

	// Initialize other non-critical components.
	// ✅ Wrap in try-catch to avoid breaking the page if something fails
	try {
		new AdsServicesMarquee().initMarquee();
	} catch (error) {
		console.error('Failed to initialize AdsServicesMarquee:', error);
	}

	// move picture inside accordion when width < 990px --- it is a $grid-reackpoint
	setupAccordionPictureRelocator('first_accordion_section', 992);
	setupAccordionPictureRelocator('second_accordion_section', 992);
	setupAccordionPictureRelocator('third_accordion_section', 992);

	// On mobile the three sections merge visually into one large accordion —
	// enforce single-open behaviour across all three.
	setupUnifiedAccordionGroup(
		['first_accordion_section', 'second_accordion_section', 'third_accordion_section'],
		991
	);

	// Reveal decorative background circles after window.load — avoids filter:blur paint during LCP
	var coverBg = document.querySelector('.sb-cover-bg');
	if (coverBg) {
		setTimeout(function (){
			coverBg.style.display = 'flex';
			coverBg.classList.add('is-visible');
			setupCoverBgScrollPause(coverBg);
		}, 300)
	}
}

// The 6 .sb-cover-bg_circle layers run an infinite blur+transform animation, which is expensive
// to keep compositing on iOS Safari. Pause the animation (keep the blur/visuals static) once the
// hero background is scrolled out of view, and resume it when it re-enters — this was causing
// sustained GPU load, scroll jank and GPU-process white-screen crashes on iPhone Safari.
function setupCoverBgScrollPause(coverBg) {
	if (!coverBg || !('IntersectionObserver' in window)) return;

	var io = new IntersectionObserver(function (entries) {
		entries.forEach(function (entry) {
			coverBg.classList.toggle('is-paused', !entry.isIntersecting);
		});
	}, { rootMargin: '200px 0px' });

	io.observe(coverBg);
}


/**
 * This event fires when the entire page has fully loaded, including all
 * dependent resources such as stylesheets and images.
 * ✅ Using requestIdleCallback instead of setTimeout for better optimization
 */
window.addEventListener('load', function () {
	// Use requestIdleCallback to ensure the main thread is free
	if ('requestIdleCallback' in window) {
		requestIdleCallback(initHeavyScripts, { timeout: 2000 });
	} else {
		// Fallback for older browsers
		setTimeout(initHeavyScripts, 200);
	}
}, { once: true, passive: true });
